import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Plane, Hotel, Bus, MapPin, Sparkles } from 'lucide-react';

const STORAGE_KEY = 'flight380_umrah_announcement_dismissed_v1';

/**
 * Home-page launch announcement for the Umrah Package Builder.
 * Auto-shows once per browser (localStorage gated). Closing or accepting
 * suppresses it on subsequent visits.
 */
export const UmrahAnnouncementModal = ({ onStartBuilder }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alreadyDismissed = false;
    try {
      alreadyDismissed = localStorage.getItem(STORAGE_KEY) === '1';
    } catch (_) { /* private mode */ }
    if (alreadyDismissed) return;
    // Small delay so the home page has time to render first
    const t = setTimeout(() => setOpen(true), 900);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch (_) { /* ignore */ }
    setOpen(false);
  };

  const handleCTA = () => {
    dismiss();
    if (typeof onStartBuilder === 'function') {
      // Slight defer so the dialog close animation can play
      setTimeout(() => onStartBuilder(), 80);
    }
  };

  const features = [
    { icon: Plane, label: 'Flights', tone: 'text-sky-700 bg-sky-100' },
    { icon: Hotel, label: 'Hotels', tone: 'text-amber-700 bg-amber-100' },
    { icon: Bus, label: 'Transport', tone: 'text-rose-700 bg-rose-100' },
    { icon: MapPin, label: 'Ziyarat', tone: 'text-emerald-700 bg-emerald-100' },
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <DialogContent
        className="w-[calc(100vw-1.5rem)] max-w-[420px] sm:max-w-md p-0 overflow-hidden border-0 shadow-2xl rounded-2xl bg-white max-h-[90vh] overflow-y-auto"
        data-testid="umrah-announcement-modal"
      >
        <DialogTitle className="sr-only">Umrah Package Builder — New Feature</DialogTitle>
        <DialogDescription className="sr-only">
          Build your complete Umrah package with flights, hotels near the Haram, transport and Ziyarat tours.
        </DialogDescription>

        {/* Top accent strip */}
        <div className="relative bg-gradient-to-br from-emerald-50 via-white to-amber-50 px-6 pt-7 pb-5">
          <div className="flex justify-center mb-4">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-[0.18em] uppercase text-emerald-700 bg-emerald-100/80 border border-emerald-300/60 rounded-full px-3 py-1">
              <Sparkles className="h-3 w-3" />
              New Feature
            </span>
          </div>

          {/* Kaaba illustration */}
          <div className="flex justify-center mb-3">
            <div
              className="w-20 h-20 rounded-2xl flex items-center justify-center text-5xl shadow-md"
              style={{
                background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                boxShadow: '0 10px 25px -10px rgba(15,23,42,0.5)',
              }}
              aria-hidden="true"
            >
              <span style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.4))' }}>🕋</span>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-slate-900 text-center leading-tight">
            Umrah Package Builder
          </h2>
        </div>

        {/* Body */}
        <div className="px-6 pb-6 -mt-1">
          <p className="text-sm sm:text-[15px] text-slate-600 text-center leading-relaxed">
            Build your complete Umrah package with flights to Jeddah/Medina,
            hotels near the Haram, and ground transportation — now with our new
            <span className="font-semibold text-slate-800"> Brown / Silver / Gold </span>
            quick-pick tiers.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
            {features.map(({ icon: Icon, label, tone }) => (
              <span
                key={label}
                className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-full ${tone}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </span>
            ))}
          </div>

          <Button
            onClick={handleCTA}
            data-testid="umrah-announcement-cta"
            className="w-full mt-6 h-12 text-base font-semibold rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white shadow-lg shadow-emerald-500/30 transition-all active:scale-[0.99]"
          >
            <Plane className="h-5 w-5 mr-2" />
            Start Building Your Umrah Package
          </Button>

          <button
            type="button"
            onClick={dismiss}
            data-testid="umrah-announcement-dismiss"
            className="block mx-auto mt-3 text-xs text-slate-400 hover:text-slate-600 transition-colors"
          >
            Maybe later
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UmrahAnnouncementModal;
